/**
 * CitationRow — footnote-style anchor + hover preview for cited
 * resources surfaced by AIKO chat.
 *
 * Two variants for two render contexts:
 *
 *   - `anchor` (default) — inline `[N]` superscript-style chip the chat
 *     stream slips between sentences. Hover / focus opens a Radix
 *     Popover with the full row content (title, summary, thumbnail).
 *     Click activates the citation (`onActivate` or `href`). Sized to
 *     drop into prose without breaking line height.
 *
 *   - `row` — the full citation card on its own line. Used in a
 *     "Sources" list at the bottom of an assistant message, or as a
 *     standalone reference in any consumer surface. Renders the same
 *     content the popover holds, just inline.
 *
 * Both variants share the same content vocabulary:
 *   - `index` — the citation number (or any `ReactNode`); `[1]`, `[1.2]`,
 *     a custom badge, etc.
 *   - `title` — the resource's display name. Required.
 *   - `summary?` — short one-line description.
 *   - `thumbnail?` — small image (URL or `ReactNode`) rendered before the
 *     title; truthy `string` renders as `<img>` with role-aware `alt`.
 *   - `badge?` — resource type or scope chip (e.g., `"quest"`); accepts
 *     a string or full `ReactNode` so consumers can drop in
 *     `<ExperienceTypeBadge>`-style siblings.
 *
 * Activation:
 *   - `onActivate` — fired on click (anchor variant) or row click (row
 *     variant). Pass either `onActivate` or `href`; if both are passed,
 *     `onActivate` wins and `href` is preserved as a fallback (e.g.,
 *     middle-click open in new tab still works).
 *   - `href` — passes through to a real `<a>` so middle-click + right
 *     click + cmd-click all behave as users expect.
 *
 * Composition:
 *   - `asChild` — Slot through to a custom anchor (e.g., a router
 *     `<Link>`); only meaningful when `href` is absent.
 *   - `motion={false}` — opts out of the popover entrance animation
 *     (anchor variant only). Reduced-motion users get this for free
 *     via the global `motion-reduce` rule the Tailwind preset injects.
 *
 * Mining note (A10): the v3 surface (`ChatColumn:1101+` + types in
 * `theater.ts:ReferencedResource`) keeps full structured records of
 * cited resources and renders them via `<DetailsDrawer>`. Pretty's
 * CitationRow is the smaller, in-line counterpart — content vocabulary
 * matches v3's `{ uid, title, resource_type, image_url, focus_area }`
 * field set so consumers can map records 1:1 onto props.
 */

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/* ============================================================================
 * Variants
 * ========================================================================= */

const anchorVariants = cva(
  cn(
    "inline-flex shrink-0 items-center justify-center align-baseline",
    "h-[18px] min-w-[18px] px-1 rounded-md text-[11px] font-semibold leading-none",
    "border border-border bg-card text-muted-foreground",
    "hover:border-accent/40 hover:bg-accent/10 hover:text-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
    "ring-offset-background transition-[background-color,border-color,color] duration-150",
    "no-underline tabular-nums",
  ),
);

const rowVariants = cva(
  cn(
    "group/citation flex w-full items-start gap-3 rounded-lg border border-border bg-card p-3 text-left text-sm",
    "transition-[background-color,border-color] duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "ring-offset-background",
  ),
  {
    variants: {
      interactive: {
        true: "hover:border-accent/40 hover:bg-accent/5 cursor-pointer",
        false: "cursor-default",
      },
    },
    defaultVariants: { interactive: false },
  },
);

/* ============================================================================
 * Types
 * ========================================================================= */

export type CitationRowVariant = "anchor" | "row";

export interface CitationRowProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "title" | "onClick"
> {
  /**
   * Citation number or label rendered in the anchor chip and as the
   * row's leading marker. Conventionally a 1-indexed integer.
   */
  index: React.ReactNode;
  /** Resource title. Required for the row body and the anchor's accessible name. */
  title: string;
  /** Optional short description shown beneath the title in the row body. */
  summary?: string;
  /**
   * Optional thumbnail. A `string` is treated as an image URL and
   * rendered as `<img>` with the title as alt text; any `ReactNode` is
   * rendered as-is so consumers can drop in their own avatar primitive.
   */
  thumbnail?: React.ReactNode;
  /**
   * Optional badge (resource type, scope, source). A `string` renders
   * as a flat pill; any `ReactNode` is rendered as-is so consumers can
   * drop in `<Badge>` / `<ExperienceTypeBadge>`.
   */
  badge?: React.ReactNode;
  /**
   * Click handler. Wins over `href` for non-modified clicks (middle-
   * click and cmd-click still follow `href` if present).
   */
  onActivate?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * Optional href. Passes through to a real `<a>` so user expectations
   * for middle-click / right-click / cmd-click hold.
   */
  href?: string;
  /**
   * Visual treatment. `anchor` (default) is the inline `[N]` chip with
   * popover preview; `row` is the full card on its own line.
   */
  variant?: CitationRowVariant;
  /**
   * Slot through the anchor or row to a custom child (e.g., a router
   * `<Link>`). Only meaningful when `href` is absent.
   */
  asChild?: boolean;
  /**
   * Disable the popover entrance animation (anchor variant only).
   * `prefers-reduced-motion` forces the disabled path regardless.
   */
  motion?: boolean;
  /**
   * Override the popover's anchored side. Default `top` so the preview
   * pops above the inline anchor without reflowing nearby text.
   */
  side?: "top" | "right" | "bottom" | "left";
  /**
   * Override the accessible name applied to the anchor chip.
   * Default: `Citation N: <title>`. Useful when the consumer's chip copy
   * differs from the resource's title.
   */
  "aria-label"?: string;
  /**
   * Replace the popover preview body (anchor variant only). When provided,
   * this node renders inside the hover/focus popover instead of the default
   * `index / title / summary / thumbnail / badge` layout - a composition seam
   * for consumers that need richer preview content (e.g. a source preview with
   * confidence + freshness). Ignored for the `row` variant.
   */
  renderPreview?: React.ReactNode;
}

/* ============================================================================
 * Component
 * ========================================================================= */

export const CitationRow = React.forwardRef<HTMLElement, CitationRowProps>(function CitationRow(
  {
    className,
    index,
    title,
    summary,
    thumbnail,
    badge,
    onActivate,
    href,
    variant = "anchor",
    asChild = false,
    motion: enableMotion = true,
    side = "top",
    "aria-label": ariaLabel,
    renderPreview,
    ...rest
  },
  ref,
) {
  const reduced = useReducedMotion();
  const shouldAnimate = enableMotion && reduced !== true;

  if (variant === "row") {
    return (
      <RowSurface
        ref={ref as React.Ref<HTMLDivElement>}
        index={index}
        title={title}
        summary={summary}
        thumbnail={thumbnail}
        badge={badge}
        onActivate={onActivate}
        href={href}
        asChild={asChild}
        ariaLabel={ariaLabel}
        className={className}
        {...rest}
      />
    );
  }

  // Tooltip-style hover/focus open. Radix Tooltip's trigger does NOT
  // intercept clicks (unlike Popover.Trigger), so `href` navigation +
  // `onActivate` fire as the user expects on the underlying anchor.
  // Self-provide the Provider so consumers can drop a CitationRow into
  // any tree without setup; nested providers compose cleanly.
  return (
    <TooltipPrimitive.Provider delayDuration={250} skipDelayDuration={300}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <AnchorChip
            ref={ref as React.Ref<HTMLAnchorElement | HTMLButtonElement>}
            index={index}
            title={title}
            onActivate={onActivate}
            href={href}
            asChild={asChild}
            ariaLabel={ariaLabel}
            className={className}
            {...rest}
          />
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className={cn(
              "z-50 w-72 max-w-[calc(100vw-1rem)] rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-md",
              shouldAnimate &&
                cn(
                  "animate-in fade-in-0 zoom-in-95",
                  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                  "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1",
                  "data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1",
                ),
            )}
          >
            {renderPreview ?? (
              <RowContent
                index={index}
                title={title}
                summary={summary}
                thumbnail={thumbnail}
                badge={badge}
              />
            )}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
});

CitationRow.displayName = "CitationRow";

/* ============================================================================
 * Internal — anchor chip (the inline `[N]` element)
 * ========================================================================= */

interface AnchorChipProps extends Omit<React.HTMLAttributes<HTMLElement>, "onClick"> {
  index: React.ReactNode;
  title: string;
  onActivate?: (event: React.MouseEvent<HTMLElement>) => void;
  href?: string;
  asChild: boolean;
  ariaLabel?: string;
}

const AnchorChip = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, AnchorChipProps>(
  function AnchorChip(
    { index, title, onActivate, href, asChild, ariaLabel, className, children, ...rest },
    ref,
  ) {
    const accessibleName = ariaLabel ?? `Citation ${stringifyIndex(index)}: ${title}`;
    const display = (
      <>
        <span aria-hidden>[</span>
        <span>{index}</span>
        <span aria-hidden>]</span>
      </>
    );

    // Modifier keys preserve the browser default (new tab / save as / etc.);
    // a plain left-click hands off to `onActivate`.
    const handlePlainClick = (event: React.MouseEvent<HTMLElement>) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!onActivate) return;
      event.preventDefault();
      onActivate(event);
    };

    if (asChild) {
      return (
        <Slot
          ref={ref as React.Ref<HTMLElement>}
          aria-label={accessibleName}
          className={cn(anchorVariants(), className)}
          {...rest}
        >
          {children ?? <span>{display}</span>}
        </Slot>
      );
    }

    if (href != null) {
      return (
        <a
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          aria-label={accessibleName}
          onClick={handlePlainClick}
          className={cn(anchorVariants(), className)}
        >
          {display}
        </a>
      );
    }

    return (
      <button
        {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        aria-label={accessibleName}
        onClick={(event) => onActivate?.(event)}
        className={cn(anchorVariants(), className)}
      >
        {display}
      </button>
    );
  },
);

/* ============================================================================
 * Internal — row surface (the inline card variant)
 * ========================================================================= */

interface RowSurfaceProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "onClick"> {
  index: React.ReactNode;
  title: string;
  summary?: string;
  thumbnail?: React.ReactNode;
  badge?: React.ReactNode;
  onActivate?: (event: React.MouseEvent<HTMLElement>) => void;
  href?: string;
  asChild: boolean;
  ariaLabel?: string;
}

const RowSurface = React.forwardRef<HTMLDivElement, RowSurfaceProps>(function RowSurface(
  {
    index,
    title,
    summary,
    thumbnail,
    badge,
    onActivate,
    href,
    asChild,
    ariaLabel,
    className,
    children,
    ...rest
  },
  ref,
) {
  const interactive = onActivate != null || href != null;
  const accessibleName = ariaLabel ?? title;
  const body = (
    <RowContent index={index} title={title} summary={summary} thumbnail={thumbnail} badge={badge} />
  );

  const handlePlainClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!onActivate) return;
    event.preventDefault();
    onActivate(event);
  };

  if (asChild) {
    return (
      <Slot
        ref={ref as React.Ref<HTMLDivElement>}
        aria-label={accessibleName}
        className={cn(rowVariants({ interactive }), className)}
        {...rest}
      >
        {children ?? <div>{body}</div>}
      </Slot>
    );
  }

  if (href != null) {
    return (
      <a
        ref={ref as unknown as React.Ref<HTMLAnchorElement>}
        href={href}
        aria-label={accessibleName}
        onClick={handlePlainClick}
        className={cn(rowVariants({ interactive }), "no-underline", className)}
        {...(rest as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {body}
      </a>
    );
  }

  if (interactive) {
    return (
      <button
        ref={ref as unknown as React.Ref<HTMLButtonElement>}
        type="button"
        aria-label={accessibleName}
        onClick={(event) => onActivate?.(event)}
        className={cn(rowVariants({ interactive }), className)}
        {...(rest as unknown as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {body}
      </button>
    );
  }

  return (
    <div
      ref={ref}
      aria-label={accessibleName}
      className={cn(rowVariants({ interactive }), className)}
      {...rest}
    >
      {body}
    </div>
  );
});

/* ============================================================================
 * Internal — shared content layout (used by row + popover)
 * ========================================================================= */

interface RowContentProps {
  index: React.ReactNode;
  title: string;
  summary?: string;
  thumbnail?: React.ReactNode;
  badge?: React.ReactNode;
}

function RowContent({ index, title, summary, thumbnail, badge }: RowContentProps) {
  return (
    <>
      {thumbnail != null ? (
        <div className="shrink-0">
          {typeof thumbnail === "string" ? (
            <img
              src={thumbnail}
              alt=""
              loading="lazy"
              className="h-10 w-10 rounded-md border border-border bg-muted object-cover"
            />
          ) : (
            thumbnail
          )}
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-md border border-border bg-card px-1 text-[10px] font-semibold text-muted-foreground tabular-nums"
          >
            <span aria-hidden>[</span>
            <span>{index}</span>
            <span aria-hidden>]</span>
          </span>
          <span className="truncate text-sm font-medium text-foreground">{title}</span>
          {badge != null ? <span className="ml-auto shrink-0">{renderBadge(badge)}</span> : null}
        </div>
        {summary ? (
          <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{summary}</p>
        ) : null}
      </div>
    </>
  );
}

function renderBadge(badge: React.ReactNode) {
  if (typeof badge === "string") {
    return (
      <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {badge}
      </span>
    );
  }
  return badge;
}

/* ============================================================================
 * Internal — stringify the index for the accessible name
 * ========================================================================= */

function stringifyIndex(index: React.ReactNode): string {
  if (typeof index === "string" || typeof index === "number") return String(index);
  return "";
}

/* ============================================================================
 * Variant exports for consumers that want the cva escape hatch
 * ========================================================================= */

export { anchorVariants as citationAnchorVariants, rowVariants as citationRowVariants };
export type CitationRowVariantProps = VariantProps<typeof rowVariants>;
